export const colors = {
  primary: '#4A6BFF',
  secondary: '#6C7A93',
  background: '#FFFFFF',  
  backgroundGradient: 'linear-gradient(180deg, #F5F7FF 0%, #FFFFFF 100%)',
  borderLight: '#f7f7f8',
  textPrimary: '#3A3C3F',
  textSecondary: '#93919f',
  secondaryBackground: '#6C94EB',
  signInGradient: 'linear-gradient(180deg, #6C94EB 0%, #2563EB 100%)',
  signInTextGradient: 'linear-gradient(180deg, rgba(255,255,255,1) 20.45%, rgba(255,255,255,0.7) 77.27%)',
  radialGradient: 'radial-gradient(circle, rgba(246, 248, 254, 1) 0%, rgba(234, 240, 255, 1) 100%)',
  text: {
    primary: '#3A3C3F',
    secondary: '#757384',
    light: '#697386'
  }
};

export const typography = {
  fontFamily: "'Inter', sans-serif",
  fontSizeMd: '16px',
  lineHeightMd: '20px',
  lineHeightLg: '30px',
  letterSpacingXs: '-0.16px',
  letterSpacingSm: '-0.3px',
  sizes: {
    h1: '48px',
    h2: '36px',
    h3: '20px',
    body: '16px',
    small: '14px'
  },
  weights: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700
  }
};

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  xxxl: '100px',
  xxxxl: '120px',
  navPadding: '16px 40px',
  gapLg: '32px',
  signInButtonPadding: '10px 20px',
  signInButtonRadius: '109px',
  signInTextContainerWidth: '76px',
  signInTextContainerHeight: '22px',
  signInTextLeft: '12px',
  signInTextTop: '30px',
  logoWidth: '159px',
  logoHeight: '39px',
  radiusXL: '56px',
  maxContentWidth: '1200px'
};

export const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '16px',
  xl: '24px'
};

export const breakpoints = {
  mobile: '320px',
  tablet: '768px',
  desktop: '1024px',
  wide: '1440px'
};

// ---------------------------------------------------------------------------
// Redesign tokens (Support Center refresh, 2026). Additive — existing exports
// above are left untouched so legacy components keep working. New/restyled
// components consume these.
// ---------------------------------------------------------------------------
export const ui = {
  brand: '#4A6BFF',      // primary brand blue (== colors.primary)
  brandInk: '#2f44c4',   // darker brand for hover / text on tint
  brandTint: '#eef1ff',  // light brand wash for icon chips / active states
  brandTint2: '#e3e8ff',
  warm: '#F59E0B',       // warm secondary accent (sparingly)
  warmInk: '#b45309',
  warmTint: '#fef4e3',
  ink: '#1f2433',        // headings
  text: '#3a3c43',       // body
  muted: '#6b7280',      // secondary text
  faint: '#9aa0ac',      // hints / captions
  line: '#ebecf2',       // hairline borders
  soft: '#f7f8fc',       // subtle section background
  soft2: '#f1f3fb',
  radius: '14px',        // cards
  radiusSm: '10px',      // controls
  maxWidth: '1140px',
  headerHeight: '64px',
  shadow: '0 1px 2px rgba(16,24,40,.04), 0 10px 28px rgba(16,24,40,.06)',
  shadowHi: '0 16px 40px rgba(16,24,40,.13)',
};

export interface Accent { color: string; tint: string; }

// Muted per-section accents — used for the section dot + card icon chip.
// Headings stay ink-colored; only these small elements pick up the hue.
export const sectionAccents: Record<string, Accent> = {
  // parent landing sections
  administrative: { color: '#4A6BFF', tint: '#eef1ff' }, // blue
  appFeatures:    { color: '#7C5CF0', tint: '#f0ecfd' }, // violet
  commonApp:      { color: '#0E9F8B', tint: '#e1f4f0' }, // teal
  compliance:     { color: '#E08A0B', tint: '#fcf1dc' }, // amber
  // admin hub groups
  courses:        { color: '#4A6BFF', tint: '#eef1ff' },
  events:         { color: '#7C5CF0', tint: '#f0ecfd' },
  members:        { color: '#0E9F8B', tint: '#e1f4f0' },
  money:          { color: '#E08A0B', tint: '#fcf1dc' },
  comms:          { color: '#D6568C', tint: '#fbe9f1' }, // rose
};
